// src/pages/CashAnalysis.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiGet } from "../api/client";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/solid";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { companyRoute } from "../utils/routeHelpers";
import { ymdTodayLocal } from "../utils/dateUtils";
import { formatMoney } from "../utils/formatNumber";
import { ReportDateRangeFilter } from "../components/ReportDateRangeFilter";
import { A } from "../styles/appleClasses";

type CashAccount = {
  account: string;
  opening: number;
  in: number;
  out: number;
  closing: number;
};

type CashProjection = {
  period: string;
  receivables_due: number;
  payables_due: number;
  projected_cash: number;
};

type CashOutlook = {
  cash_on_hand: number;
  receivables_outstanding: number;
  payables_outstanding: number;
  net_cash_position: number;
};

type CashAnalysisResponse = {
  accounts: CashAccount[];
  outlook?: CashOutlook;
  projection: CashProjection[];
};

function money(n: number | undefined | null) {
  return typeof n === "number" && !Number.isNaN(n)
    ? formatMoney(n, "$", { minDp: 0, maxDp: 2 })
    : "—";
}

function getYearStart() {
  const d = new Date();
  return `${d.getFullYear()}-01-01`;
}

export default function CashAnalysisPage() {
  const { t } = useTranslation();
  const [accounts, setAccounts] = useState<CashAccount[]>([]);
  const [projection, setProjection] = useState<CashProjection[]>([]);
  const [outlook, setOutlook] = useState<CashOutlook | null>(null);
  const [expandedAccounts, setExpandedAccounts] = useState<
    Record<string, boolean>
  >({});
  const [startDate, setStartDate] = useState(() => getYearStart());
  const [endDate, setEndDate] = useState(() => ymdTodayLocal());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const { company_id = "" } = useParams<{ company_id: string }>();

  const todayStr = useMemo(() => ymdTodayLocal(), []);
  const cashTotal = useMemo(
    () => accounts.reduce((sum, acc) => sum + (acc.closing || 0), 0),
    [accounts],
  );
  const sortedAccounts = useMemo(() => {
    const byName = [...accounts].sort((a, b) =>
      a.account.localeCompare(b.account, undefined, { sensitivity: "base" }),
    );

    const cash = byName.filter(
      (a) => a.account.trim().toLowerCase() === "cash",
    );
    const others = byName.filter(
      (a) => a.account.trim().toLowerCase() !== "cash",
    );

    return [...cash, ...others];
  }, [accounts]);

  const projectionColumns = useMemo(() => {
    const sorted = [...projection].sort((a, b) => {
      const aDays = Number.parseInt(a.period, 10);
      const bDays = Number.parseInt(b.period, 10);
      return (
        (Number.isNaN(aDays) ? 999 : aDays) -
        (Number.isNaN(bDays) ? 999 : bDays)
      );
    });

    return [
      {
        period: "Now",
        receivables_due: 0,
        payables_due: 0,
        projected_cash: cashTotal,
      },
      ...sorted,
    ];
  }, [projection, cashTotal]);

  useEffect(() => {
    let mounted = true;

    setLoading(true);
    setError(null);

    apiGet<CashAnalysisResponse>(
      `/reports/cash-analysis?start_date=${startDate}&end_date=${endDate}`,
    )
      .then((data) => {
        if (!mounted) return;
        setAccounts(data?.accounts ?? []);
        setOutlook(data?.outlook ?? null);
        setProjection(data?.projection ?? []);
      })
      .catch(() => {
        if (!mounted) return;
        setError("Failed to load cash analysis.");
        setAccounts([]);
        setOutlook(null);
        setProjection([]);
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [startDate, endDate]);

  // Translate only known presentation labels; keep API values, keys and order intact.
  const periodLabel = (period: string) => {
    switch (period) {
      case "10 days": return t("cashAnalysis.periods.days10");
      case "20 days": return t("cashAnalysis.periods.days20");
      case "30 days": return t("cashAnalysis.periods.days30");
      default: return period;
    }
  };

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "hover:bg-gray-100 active:bg-gray-200 " +
    "focus:outline-none focus:ring-2 focus:ring-appleblueD";

  const TitleBar = () => (
    <div className="sticky top-0 z-30 bg-gray-50 w-full max-w-3xl flex items-center border-b border-gray-100 px-2 pb-2 mb-2">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className={iconBtn}
        aria-label={t("directory.common.back")}
      >
        <ArrowLeftIcon className="w-5 h-5 text-gray-700" />
      </button>

      <div className="flex-1 flex justify-center pointer-events-none relative">
        <h2 className={`${A.title} text-center`}>{t("cashAnalysis.title")}</h2>
      </div>

      <button
        type="button"
        onClick={() => navigate(companyRoute(company_id, "/"))}
        className={iconBtn}
        aria-label={t("directory.common.close")}
      >
        <XMarkIcon className="w-5 h-5 text-gray-700" />
      </button>
    </div>
  );

  const toggleAccountExpanded = (account: string) => {
    setExpandedAccounts((prev) => ({
      ...prev,
      [account]: !prev[account],
    }));
  };

  const SectionTitle = ({
    title,
    right,
  }: {
    title: string;
    right?: React.ReactNode;
  }) => (
    <div className="flex items-baseline justify-between mb-3">
      <div className={A.title}>{title}</div>
      {right}
    </div>
  );

  return (
    <div
      className={`min-h-screen bg-gray-50 ${A.body} flex flex-col items-center pt-4`}
    >
      <TitleBar />

      <div className="w-full max-w-3xl px-2">
        <ReportDateRangeFilter
          fromLabel={t("sales.shared.from")}
          toLabel={t("sales.shared.to")}
          openCalendarLabel={t("sales.shared.openCalendar")}
          companyId={company_id}
          startDate={startDate}
          endDate={endDate}
          onChange={({ startDate, endDate }) => {
            setStartDate(startDate);
            setEndDate(endDate);
          }}
        />
        {loading ? (
          <div className="flex justify-center items-center min-h-[30vh] text-gray-400">
            {t("directory.common.loading")}
          </div>
        ) : error ? (
          <div className="text-[13px] font-medium text-red-600">{error === "Failed to load cash analysis." ? t("cashAnalysis.errors.load") : error}</div>
        ) : (
          <div className="space-y-3 mb-10">
            {/* Cash Accounts */}
            <div className={`${A.card} p-5 md:p-6`}>
              <SectionTitle title={t("cashAnalysis.cashAccounts")} />

              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-[13px] text-gray-700">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className={`py-2 text-left ${A.label} font-medium`}>
                        {t("generalJournal.account")}
                      </th>
                      <th className={`py-2 text-right ${A.label} font-medium`}>
                        {t("generalLedger.opening")}
                      </th>
                      <th className={`py-2 text-right ${A.label} font-medium`}>
                        {t("cashAnalysis.cashIn")}
                      </th>
                      <th className={`py-2 text-right ${A.label} font-medium`}>
                        {t("cashAnalysis.cashOut")}
                      </th>
                      <th className={`py-2 text-right ${A.label} font-medium`}>
                        {t("cashAnalysis.closing")}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {sortedAccounts.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="py-4 text-center text-gray-400"
                        >
                          —
                        </td>
                      </tr>
                    ) : (
                      sortedAccounts.map((acc) => (
                        <tr
                          key={acc.account}
                          className="border-b border-gray-100 last:border-b-0"
                        >
                          <td className="py-2 pr-2 text-gray-700">
                            {acc.account}
                          </td>
                          <td className={`py-2 text-right ${A.num}`}>
                            {money(acc.opening)}
                          </td>
                          <td className={`py-2 text-right ${A.num}`}>
                            {money(acc.in)}
                          </td>
                          <td className={`py-2 text-right ${A.num}`}>
                            {money(acc.out)}
                          </td>
                          <td
                            className={`py-2 text-right ${A.num} font-medium text-gray-700`}
                          >
                            {money(acc.closing)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>

                <div className="mt-4 border-t border-gray-100 pt-3 flex items-center justify-between">
                  <span className="font-medium text-gray-700">
                    {t("cashAnalysis.totalCashOnHand")}
                  </span>
                  <span className={`${A.num} font-semibold text-gray-700`}>
                    {money(cashTotal)}
                  </span>
                </div>
              </div>

              {/* Mobile stacked cards */}
              <div className="md:hidden space-y-2">
                {sortedAccounts.length === 0 ? (
                  <div className="py-4 text-center text-gray-400">—</div>
                ) : (
                  sortedAccounts.map((acc) => {
                    const isExpanded = !!expandedAccounts[acc.account];

                    return (
                      <div
                        key={acc.account}
                        className="rounded-2xl border border-gray-100 bg-white p-4"
                      >
                        <button
                          type="button"
                          onClick={() => toggleAccountExpanded(acc.account)}
                          className="flex w-full items-center justify-between gap-3 text-left"
                          aria-expanded={isExpanded}
                        >
                          <div className="min-w-0">
                            <div className="font-medium text-gray-700 truncate">
                              {acc.account}
                            </div>
                            <div className={A.label}>{t("cashAnalysis.closingBalance")}</div>
                          </div>

                          <div className="flex items-center gap-2">
                            <span
                              className={`${A.num} font-semibold text-gray-700`}
                            >
                              {money(acc.closing)}
                            </span>

                            {isExpanded ? (
                              <ChevronDownIcon className="h-5 w-5 text-appleblue" />
                            ) : (
                              <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                        </button>

                        {isExpanded && (
                          <div className="mt-3 space-y-3 border-t border-gray-100 pt-3">
                            <div className="flex items-center justify-between pr-10">
                              <span className={A.label}>{t("generalLedger.opening")}</span>
                              <span className={`${A.num} text-gray-700`}>
                                {money(acc.opening)}
                              </span>
                            </div>

                            <div className="flex items-center justify-between pr-10">
                              <span className={A.label}>{t("cashAnalysis.cashIn")}</span>
                              <span className={`${A.num} text-gray-700`}>
                                {money(acc.in)}
                              </span>
                            </div>

                            <div className="flex items-center justify-between pr-10">
                              <span className={A.label}>{t("cashAnalysis.cashOut")}</span>
                              <span className={`${A.num} text-gray-700`}>
                                {money(acc.out)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}

                <div className="mt-4 border-t border-gray-100 pt-3 flex items-center justify-between">
                  <span className="font-medium text-gray-700">
                    {t("cashAnalysis.totalCashOnHand")}
                  </span>
                  <span className={`${A.num} font-semibold text-gray-700`}>
                    {money(cashTotal)}
                  </span>
                </div>
              </div>
            </div>

            {/* Cash Outlook */}
            <div className={`${A.card} p-5 md:p-6`}>
              <SectionTitle title={t("cashAnalysis.cashOutlook")} />

              <div className={`${A.label} mb-3`}>
                {t("cashAnalysis.openReceivablesPayables")}
              </div>

              {outlook ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                    <span className="text-gray-600">{t("cashAnalysis.cashOnHand")}</span>
                    <span className={`${A.num} font-medium text-gray-700`}>
                      {money(outlook.cash_on_hand)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                    <span className="text-gray-600">
                      {t("cashAnalysis.receivablesOutstanding")}
                    </span>
                    <span className={`${A.num} font-medium text-gray-700`}>
                      {money(outlook.receivables_outstanding)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                    <span className="text-gray-600">{t("cashAnalysis.payablesOutstanding")}</span>
                    <span className={`${A.num} font-medium text-gray-700`}>
                      {money(outlook.payables_outstanding)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between pt-1">
                    <span className="font-medium text-gray-700">
                      {t("cashAnalysis.netCashPosition")}
                    </span>
                    <span className={`${A.num} font-semibold text-gray-700`}>
                      {money(outlook.net_cash_position)}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="py-4 text-center text-gray-400">—</div>
              )}

              {projection.length > 0 && (
                <div className="mt-5 border-t border-gray-100 pt-4">
                  <div className={`${A.label} mb-3 font-medium`}>{t("cashAnalysis.dueDateOutlook")}</div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-[13px] text-gray-700">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th
                            className={`py-2 text-left ${A.label} font-medium`}
                          >
                            {t("cashAnalysis.dueWithin")}
                          </th>
                          {projection.map((p) => (
                            <th
                              key={p.period}
                              className={`py-2 text-center ${A.label} font-medium`}
                            >
                              {periodLabel(p.period)}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        <tr className="border-b border-gray-100">
                          <td className="py-2 font-medium text-gray-700">
                            {t("balanceSheet.receivables")}
                          </td>
                          {projection.map((p) => (
                            <td
                              key={p.period}
                              className={`py-2 text-center ${A.num} font-medium text-gray-700`}
                            >
                              {money(p.receivables_due)}
                            </td>
                          ))}
                        </tr>

                        <tr>
                          <td className="py-2 font-medium text-gray-700">
                            {t("balanceSheet.payables")}
                          </td>
                          {projection.map((p) => (
                            <td
                              key={p.period}
                              className={`py-2 text-center ${A.num} font-medium text-gray-700`}
                            >
                              {money(p.payables_due)}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
